// The Games played report's view logic (F-CAT-04, spec/17 R-04), pure: how days fold into weeks, and each row's share
// of the busiest — so the page only renders. The range is the Reports screen's (`report-search.ts`).
//
// Days are the venue's business days (the server buckets plays by the venue's timezone and cutoff); a week is the
// Monday-to-Sunday week a day falls in, named by its Monday.

import type { GamesPlayedReport, LocalDate } from '@clavian/api-client';

/** `date` moved by `days`, as `YYYY-MM-DD`. */
function shiftLocalDate(date: string, days: number): LocalDate {
  const at = new Date(`${date}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10) as LocalDate;
}

/** The Monday of the week `date` falls in. */
export function weekOf(date: LocalDate): LocalDate {
  // getUTCDay is 0 = Sunday; Monday starts the week.
  const offset = (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
  return shiftLocalDate(date, -offset);
}

/** One bar of the time chart: a day or a week, its seconds, and its top title. */
export interface PeriodRow {
  readonly key: LocalDate;
  readonly seconds: number;
  readonly topTitleId: string | null;
}

/** The report's days as rows, or folded into Monday-to-Sunday weeks. Oldest first. */
export function periodsOf(report: Pick<GamesPlayedReport, 'days'>, by: 'day' | 'week'): PeriodRow[] {
  const buckets = new Map<LocalDate, Map<string, number>>();
  const totals = new Map<LocalDate, number>();
  for (const day of report.days) {
    const key = by === 'day' ? day.date : weekOf(day.date);
    const bucket = buckets.get(key) ?? new Map<string, number>();
    for (const share of day.titles) bucket.set(share.titleId, (bucket.get(share.titleId) ?? 0) + share.seconds);
    buckets.set(key, bucket);
    totals.set(key, (totals.get(key) ?? 0) + day.seconds);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, bucket]) => {
      let topTitleId: string | null = null;
      let topSeconds = 0;
      for (const [titleId, seconds] of bucket) {
        if (seconds > topSeconds) {
          topTitleId = titleId;
          topSeconds = seconds;
        }
      }
      return { key, seconds: totals.get(key) ?? 0, topTitleId };
    });
}

/**
 * The periods over the whole range, oldest first: a day (or week) nobody played is a zero column, not a gap the chart
 * closes up — so 25 Sep and 1 Oct never sit side by side as if they were neighbours.
 */
export function periodsInRange(report: Pick<GamesPlayedReport, 'days'>, by: 'day' | 'week', from: string, to: string): PeriodRow[] {
  const played = new Map(periodsOf(report, by).map((row) => [row.key, row]));
  const keys: LocalDate[] = [];
  for (let day = from as LocalDate; day <= to; day = shiftLocalDate(day, 1)) {
    const key = by === 'day' ? day : weekOf(day);
    if (keys[keys.length - 1] !== key) keys.push(key);
    if (keys.length > 400) break;
  }
  return keys.map((key) => played.get(key) ?? { key, seconds: 0, topTitleId: null });
}

/** Each value's share of the largest, 0–1, for a bar's length. */
export function shareOf(seconds: number, largest: number): number {
  return largest <= 0 ? 0 : Math.min(1, Math.max(0, seconds / largest));
}

/**
 * Play time in the unit a venue reads it in: minutes under an hour ("48 min", never "0.8 h" or "0 h"), hours with one
 * decimal from an hour up ("12.5 h"). Zero stays zero minutes.
 */
export function playTimeOf(seconds: number): { readonly unit: 'minutes' | 'hours'; readonly value: number } {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return { unit: 'minutes', value: seconds > 0 ? Math.max(1, minutes) : 0 };
  return { unit: 'hours', value: Math.round(seconds / 360) / 10 };
}

/** Seconds as hours with one decimal, the unit a venue reads play time in ("12.5 h"). */
export function hoursOf(seconds: number): number {
  return Math.round((seconds / 3600) * 10) / 10;
}
